import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import { Btn, Modal, Input } from '../../components/UI';
import api from '../../utils/api';

// ── Animated counter hook ──
function useCounter(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

// ── Intersection observer hook ──
function useVisible(threshold = 0.2) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const FEATURES = [
  { icon: '📤', title: 'WhatsApp Campaign Management', desc: 'Send personalised WhatsApp campaigns to thousands of customers at once. Smart auto-rotation keeps your numbers safe and delivery high.', color: '#00E74C' },
  { icon: '👥', title: 'Built-in CRM System', desc: 'Store every lead automatically. Track deals through a full pipeline — New → Contacted → Interested → Negotiation → Closed.', color: '#4da8ff' },
  { icon: '🔌', title: 'Website Lead Capture Widget', desc: 'One line of code on your website. Visitors submit their details and the lead hits your WhatsApp instantly — zero delay.', color: '#ff6b6b', featured: true },
  { icon: '🤖', title: 'Auto Reply Bot', desc: 'Keyword-based instant replies 24/7. Set welcome messages, fallback responses, and outside-hours replies automatically.', color: '#ffd700' },
  { icon: '📊', title: 'Campaign Analytics', desc: 'Real-time tick tracking — see exactly who received, who opened, and who read your messages. Full campaign performance reports.', color: '#a78bfa' },
  { icon: '🔄', title: 'Auto Number Rotation', desc: 'Automatically rotates between your WhatsApp numbers every 10 messages with human-like delays for maximum deliverability.', color: '#00E74C' },
];

const HOW_IT_WORKS = [
  { step: '01', icon: '🔌', title: 'Install Widget', desc: 'Copy one line of code and paste it on your website. Works on any platform — WordPress, Shopify, or custom HTML.' },
  { step: '02', icon: '📋', title: 'Capture Leads', desc: 'Visitors fill in their name and WhatsApp number. The lead is captured instantly and saved to your CRM.' },
  { step: '03', icon: '📱', title: 'Instant WhatsApp Alert', desc: 'You receive the lead directly on WhatsApp in real time. No delay, no email — straight to your phone.' },
  { step: '04', icon: '🚀', title: 'Convert Faster', desc: 'Reply immediately while interest is high. Close more deals by being the first to respond.' },
];

const TESTIMONIALS = [
  { name: 'Rajesh Kumar', role: 'Real Estate Agent, Chennai', text: 'MS Reach changed how I handle leads. I used to miss calls — now every website visitor becomes a WhatsApp conversation instantly.', avatar: 'RK' },
  { name: 'Priya Sharma', role: 'E-commerce Owner, Bangalore', text: 'The auto reply bot handles customer queries at midnight. My sales went up 40% because customers always get an instant response.', avatar: 'PS' },
  { name: 'Mohammed Ali', role: 'Digital Agency, Hyderabad', text: 'I manage 10+ clients on MS Reach. The campaign manager and CRM together save me 3 hours every day. Worth every rupee.', avatar: 'MA' },
];

const FAQS = [
  { q: 'Do I need a WhatsApp Business API account?', a: 'No! MS Reach works with your regular WhatsApp number. Just scan the QR code once and you are ready to send campaigns.' },
  { q: 'How does the website widget work?', a: 'You get a single script tag to add to your website. When a visitor submits the form, the lead is instantly sent to your WhatsApp and saved in your CRM.' },
  { q: 'Is my WhatsApp number safe?', a: 'Yes. We use smart rotation (every 10 messages), random delays (30-60 seconds), and a daily limit of 1000 messages per number — all best practices to keep numbers safe.' },
  { q: 'Can I try before I buy?', a: 'Yes — book a free demo and we will walk you through the entire platform live. No credit card required.' },
  { q: 'Do you provide GST invoices?', a: 'Absolutely. Every payment generates a proper GST invoice with CGST/SGST for Tamil Nadu clients and IGST for other states.' },
];

export default function Home() {
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);

  const [heroRef, heroVisible] = useVisible(0.1);
  const [statsRef, statsVisible] = useVisible(0.3);
  const [featRef, featVisible] = useVisible(0.1);
  const [howRef, howVisible] = useVisible(0.1);
  const [testRef, testVisible] = useVisible(0.1);

  const c1 = useCounter(5000, 2000, statsVisible);
  const c2 = useCounter(157, 1800, statsVisible);
  const c3 = useCounter(98, 2200, statsVisible);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/api/website-leads', { ...form, source: modal });
      setSent(true);
    } catch {}
    finally { setLoading(false); }
  };

  const openModal = (src) => { setModal(src); setSent(false); setForm({ name: '', email: '', phone: '' }); };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', overflow: 'hidden' }}>
      <Navbar />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section ref={heroRef} style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '120px 24px 80px', textAlign: 'center', position: 'relative' }}>
        {/* Grid bg */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.018) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        {/* Glow */}
        <div style={{ position: 'absolute', top: '25%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, background: 'radial-gradient(circle, rgba(0,231,76,0.07) 0%, transparent 65%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <div style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'none' : 'translateY(20px)', transition: 'all 0.6s ease', display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.2)', padding: '8px 18px', borderRadius: 100, fontSize: 13, color: '#fff', marginBottom: 32, animation: heroVisible ? 'borderPulse 3s ease infinite' : 'none' }}>
            <span style={{ width: 7, height: 7, background: 'var(--green)', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
            🚀 WhatsApp Marketing Platform — Now Live
          </div>

          {/* Headline */}
          <h1 style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'none' : 'translateY(30px)', transition: 'all 0.7s ease 0.1s', fontFamily: 'Sora', fontWeight: 800, fontSize: 'clamp(34px,5.5vw,72px)', lineHeight: 1.08, letterSpacing: -1.5, marginBottom: 24, color: '#fff' }}>
            Turn Website Visitors Into<br />
            <span style={{ background: 'linear-gradient(135deg, #00E74C 0%, #00ff88 50%, #00E74C 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% auto', animation: 'shimmer 3s linear infinite' }}>
              WhatsApp Leads
            </span>
            <br />Instantly
          </h1>

          {/* Sub */}
          <p style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'none' : 'translateY(20px)', transition: 'all 0.7s ease 0.2s', fontSize: 18, color: 'rgba(255,255,255,0.7)', maxWidth: 660, margin: '0 auto 44px', lineHeight: 1.75 }}>
            One line of code on your website. Leads go directly to your WhatsApp in real time. Manage campaigns, automate replies, and track every customer from one powerful platform.
          </p>

          {/* CTAs */}
          <div style={{ opacity: heroVisible ? 1 : 0, transition: 'all 0.7s ease 0.3s', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 64 }}>
            <Btn size="lg" onClick={() => openModal('request_demo')} style={{ padding: '14px 32px', fontSize: 15, boxShadow: '0 0 30px rgba(0,231,76,0.3)' }}>
              🎯 Request Free Demo
            </Btn>
            <Btn variant="outline" size="lg" onClick={() => openModal('talk_to_sales')} style={{ padding: '14px 32px', fontSize: 15, borderColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
              💬 Talk to Sales
            </Btn>
            <Link to="/register">
              <Btn variant="ghost" size="lg" style={{ padding: '14px 32px', fontSize: 15, color: 'rgba(255,255,255,0.6)' }}>
                Get Started →
              </Btn>
            </Link>
          </div>

          {/* Widget preview floating */}
          <div style={{ opacity: heroVisible ? 1 : 0, transition: 'all 0.8s ease 0.4s', display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: 'rgba(17,17,17,0.95)', border: '1px solid rgba(0,231,76,0.25)', borderRadius: 20, padding: '24px 28px', maxWidth: 340, width: '100%', boxShadow: '0 0 60px rgba(0,231,76,0.1), 0 40px 80px rgba(0,0,0,0.5)', animation: 'float 4s ease-in-out infinite' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 36, height: 36, background: 'var(--green)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💬</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>Contact Us on WhatsApp</div>
                  <div style={{ fontSize: 11, color: 'var(--green)' }}>● Online Now</div>
                </div>
              </div>
              {['Your Name', 'WhatsApp Number', 'Email ID'].map((ph, i) => (
                <div key={i} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '11px 14px', color: 'rgba(255,255,255,0.3)', fontSize: 13, marginBottom: 8 }}>{ph}</div>
              ))}
              <div style={{ background: 'var(--green)', borderRadius: 10, padding: '12px', textAlign: 'center', fontWeight: 700, fontSize: 14, color: '#000', marginTop: 4 }}>Send Message 🚀</div>
              <div style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 10 }}>Powered by <strong style={{ color: 'var(--green)' }}>MS Reach</strong> ⚡</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────── */}
      <section ref={statsRef} style={{ padding: '60px 24px', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 40, textAlign: 'center' }}>
          {[[c1, '+', 'Messages / Day', 'Per number, safely delivered'], [c2, '+', 'Platform Features', 'Everything in one place'], [c3, '%', 'Client Retention', 'Businesses that stay with us']].map(([val, suf, label, sub], i) => (
            <div key={i} style={{ opacity: statsVisible ? 1 : 0, transform: statsVisible ? 'none' : 'translateY(20px)', transition: `all 0.6s ease ${i * 0.15}s` }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 'clamp(36px,5vw,60px)', color: 'var(--green)', lineHeight: 1, animation: statsVisible ? 'countUp 0.5s ease' : 'none' }}>
                {val.toLocaleString()}{suf}
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, color: '#fff', marginTop: 8 }}>{label}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────── */}
      <section ref={howRef} style={{ padding: '100px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-block', background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.2)', color: 'var(--green)', padding: '6px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>How It Works</div>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 'clamp(26px,3.5vw,44px)', color: '#fff', marginBottom: 16 }}>From Website Visit to WhatsApp in <span style={{ color: 'var(--green)' }}>Seconds</span></h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, maxWidth: 600, margin: '0 auto' }}>No complex setup. No API keys. Just add one line of code and start capturing leads instantly.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 24, position: 'relative' }}>
            {HOW_IT_WORKS.map((item, i) => (
              <div key={i} style={{ opacity: howVisible ? 1 : 0, transform: howVisible ? 'none' : 'translateY(30px)', transition: `all 0.6s ease ${i * 0.15}s`, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 16, right: 16, fontFamily: 'Sora', fontWeight: 800, fontSize: 48, color: 'rgba(0,231,76,0.05)', lineHeight: 1 }}>{item.step}</div>
                <div style={{ width: 52, height: 52, background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.15)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 16 }}>{item.icon}</div>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 16, color: '#fff', marginBottom: 10 }}>{item.title}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{item.desc}</div>
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: 'var(--green)', color: '#000', width: 24, height: 24, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>{i + 1}</span>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', letterSpacing: 1, textTransform: 'uppercase' }}>Step {item.step}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────── */}
      <section ref={featRef} style={{ padding: '100px 24px', background: 'rgba(0,231,76,0.015)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-block', background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.2)', color: 'var(--green)', padding: '6px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>Features</div>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 'clamp(26px,3.5vw,44px)', color: '#fff', marginBottom: 16 }}>Everything You Need to <span style={{ color: 'var(--green)' }}>Grow Faster</span></h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, maxWidth: 600, margin: '0 auto' }}>A complete WhatsApp marketing suite built for Indian businesses — from solo entrepreneurs to growing teams.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 20 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{
                opacity: featVisible ? 1 : 0,
                transform: featVisible ? 'none' : i % 2 === 0 ? 'translateX(-30px)' : 'translateX(30px)',
                transition: `all 0.6s ease ${i * 0.1}s`,
                background: 'var(--bg-card)',
                border: `1px solid ${f.featured ? 'rgba(0,231,76,0.3)' : 'var(--border)'}`,
                borderRadius: 16, padding: 28, position: 'relative', overflow: 'hidden',
                cursor: 'default'
              }}
                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${f.color}40`; e.currentTarget.style.boxShadow = `0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px ${f.color}20`; }}
                onMouseOut={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = f.featured ? 'rgba(0,231,76,0.3)' : 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {/* Background glow */}
                <div style={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, background: `radial-gradient(circle, ${f.color}15 0%, transparent 70%)`, pointerEvents: 'none' }} />
                {f.featured && (
                  <span style={{ position: 'absolute', top: 16, right: 16, background: 'var(--green)', color: '#000', padding: '3px 10px', borderRadius: 100, fontSize: 10, fontWeight: 800, letterSpacing: 1 }}>★ FEATURED</span>
                )}
                <div style={{ width: 52, height: 52, background: `${f.color}15`, border: `1px solid ${f.color}30`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 16, transition: 'transform 0.3s' }}>{f.icon}</div>
                <h3 style={{ fontFamily: 'Sora', fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 10 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{f.desc}</p>
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 6, color: f.color, fontSize: 13, fontWeight: 600 }}>
                  Learn more <span>→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(0,231,76,0.06) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 700, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 'clamp(28px,4vw,52px)', color: '#fff', lineHeight: 1.15, marginBottom: 20 }}>
            Stop Losing Leads.<br />
            <span style={{ color: 'var(--green)' }}>Start Converting.</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 17, marginBottom: 40, lineHeight: 1.75 }}>
            Every visitor who leaves your website without contacting you is a lost opportunity. MS Reach bridges that gap — instantly and automatically.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Btn size="lg" onClick={() => openModal('request_demo')} style={{ padding: '14px 36px', boxShadow: '0 0 40px rgba(0,231,76,0.25)' }}>Start Free Demo</Btn>
            <Link to="/plans"><Btn variant="outline" size="lg" style={{ padding: '14px 36px', borderColor: 'rgba(255,255,255,0.15)', color: '#fff' }}>View Pricing</Btn></Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────── */}
      <section ref={testRef} style={{ padding: '100px 24px', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-block', background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.2)', color: 'var(--green)', padding: '6px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>Testimonials</div>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 'clamp(26px,3.5vw,44px)', color: '#fff' }}>Businesses That <span style={{ color: 'var(--green)' }}>Trust Us</span></h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ opacity: testVisible ? 1 : 0, transform: testVisible ? 'none' : 'translateY(30px)', transition: `all 0.6s ease ${i * 0.15}s`, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 28 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                  {[...Array(5)].map((_, j) => <span key={j} style={{ color: '#ffd700', fontSize: 16 }}>★</span>)}
                </div>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, marginBottom: 20, fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, background: 'var(--green)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora', fontWeight: 700, color: '#000', fontSize: 14 }}>{t.avatar}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────── */}
      <section style={{ padding: '100px 24px', background: 'rgba(0,0,0,0.3)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-block', background: 'rgba(0,231,76,0.08)', border: '1px solid rgba(0,231,76,0.2)', color: 'var(--green)', padding: '6px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>FAQ</div>
            <h2 style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 'clamp(26px,3.5vw,44px)', color: '#fff' }}>Frequently Asked <span style={{ color: 'var(--green)' }}>Questions</span></h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: `1px solid ${faqOpen === i ? 'rgba(0,231,76,0.3)' : 'var(--border)'}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.3s' }}>
                <button onClick={() => setFaqOpen(faqOpen === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', background: 'none', border: 'none', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', fontFamily: 'DM Sans' }}>
                  {faq.q}
                  <span style={{ color: 'var(--green)', fontSize: 20, transition: 'transform 0.3s', transform: faqOpen === i ? 'rotate(45deg)' : 'none', flexShrink: 0, marginLeft: 12 }}>+</span>
                </button>
                {faqOpen === i && (
                  <div style={{ padding: '0 20px 18px', fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, animation: 'fadeIn 0.25s ease' }}>{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ────────────────────────────────────────── */}
      <footer style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)', padding: '72px 24px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.5fr', gap: 48, marginBottom: 56 }}>
            <div>
              <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, marginBottom: 12 }}>
                <span style={{ color: 'var(--green)' }}>MS</span><span style={{ color: '#fff' }}>Reach</span>
              </div>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', lineHeight: 1.75, marginBottom: 20 }}>WhatsApp Marketing + CRM Platform. Helping Indian businesses capture more leads and convert faster.</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.9 }}>📧 halo@msdiscoveries.com<br />📞 +91 9443724724<br />🕐 Mon–Sat, 10AM–6PM IST</p>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18 }}>Product</div>
              {[['/', 'Home'], ['/plans', 'Pricing'], ['/about', 'About'], ['/contact', 'Contact']].map(([to, label]) => (
                <Link key={to} to={to} style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: 14, lineHeight: 2.2, transition: 'color 0.2s' }}
                  onMouseOver={e => e.target.style.color = 'var(--green)'}
                  onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                >{label}</Link>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18 }}>Account</div>
              {[['/login', 'Login'], ['/register', 'Register'], ['/admin/login', 'Admin Login']].map(([to, label]) => (
                <Link key={to} to={to} style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: 14, lineHeight: 2.2, transition: 'color 0.2s' }}
                  onMouseOver={e => e.target.style.color = 'var(--green)'}
                  onMouseOut={e => e.target.style.color = 'rgba(255,255,255,0.4)'}
                >{label}</Link>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18 }}>Company</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.9 }}>MS Discoveries<br />Paramathi Velur, Namakkal<br />Tamil Nadu - 638182, India<br /><br />GSTIN: 33FYQPM9398C1ZP</p>
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.25)' }}>
            <span>© 2026 MS Discoveries. All rights reserved.</span>
            <span>Capture. Connect. Convert. ⚡</span>
          </div>
        </div>
      </footer>

      {/* ── Lead Modal ────────────────────────────────────── */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'request_demo' ? '🎯 Request Free Demo' : '💬 Talk to Sales'}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
            <h3 style={{ color: 'var(--green)', marginBottom: 8 }}>Thank you!</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>We will contact you within 24 hours.</p>
            <Btn onClick={() => setModal(null)} style={{ marginTop: 20 }}>Close</Btn>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Input label="Your Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" required />
            <Input label="Email ID *" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" required />
            <Input label="Phone Number *" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 9876543210" required />
            <Btn type="submit" disabled={loading} full size="lg" style={{ marginTop: 8 }}>{loading ? 'Submitting...' : 'Submit'}</Btn>
          </form>
        )}
      </Modal>
    </div>
  );
}
